<template>
  <label class="block relative">
    <p>{{ $t("myTicket.labels.birthDate") }}</p>
    <!-- Campo falso: visible, muestra la fecha formateada -->
    <div class="relative w-full">
      <input
        ref="fake"
        aria-hidden="true"
        type="text"
        :placeholder="placeholder"
        :value="formatted"
        tabindex="-1"
        :style="{ ...getInputStyle, ...inputStyle }"
        class="w-full h-full border input-text bg-grayInput border-grayInputBorder px-4 py-2 rounded pr-10"
        @focus="openNativePicker"
        @click="openNativePicker"
        @keydown.enter.prevent="openNativePicker"
        @input="filterNumericInput"
      />
      <img
        :src="require('@/assets/images/ic-calendar.svg')"
        :alt="$t('wegow.altIcon')"
        class="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 pointer-events-none"
        style="cursor: pointer"
      />
    </div>

    <!-- Campo real: escondido pero accesible -->
    <input
      :id="id"
      ref="real"
      v-model="model"
      :name="name"
      type="date"
      class="sr-only"
      @change="syncFormatted"
    />
  </label>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useIframeStyle } from "@/composables/useIframeStyle";

const props = defineProps({
  value: {
    type: String,
    default: ""
  },
  id: {
    type: String,
    default: "input"
  },
  name: {
    type: String,
    default: "birthday"
  },
  inputStyle: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(["update:value"]);
const route = useRoute();
const real = ref(null);
const fake = ref(null);
const model = ref(props.value || "");
const formatted = ref("");
const { getInputStyle } = useIframeStyle();

// Configuración por idioma
const localeConfig = {
  es: { format: "dd/mm/yyyy", placeholder: "dd/mm/aaaa" },
  us: { format: "mm/dd/yyyy", placeholder: "mm/dd/yyyy" },
  uk: { format: "dd/mm/yyyy", placeholder: "dd/mm/yyyy" },
  gb: { format: "dd/mm/yyyy", placeholder: "dd/mm/yyyy" },
  default: { format: "dd/mm/yyyy", placeholder: "dd/mm/yyyy" }
};

// Detectar idioma según la ruta
const getLocale = () => {
  const path = route.path.toLowerCase();
  if (path.includes("/es/")) return "es";
  if (path.includes("/us/")) return "us";
  if (path.includes("/uk/")) return "uk";
  if (path.includes("/gb/")) return "gb";
  return "es"; // Por defecto, español
};

const currentLocale = getLocale();
const dateFormat = localeConfig[currentLocale].format;
const placeholder = localeConfig[currentLocale].placeholder;

// Validar fecha según el formato
const isValidDate = dateString => {
  const parts = dateString.split("/");
  let day, month, year;

  if (currentLocale === "us") {
    // mm/dd/yyyy
    [month, day, year] = parts.map(Number);
  } else {
    // dd/mm/yyyy
    [day, month, year] = parts.map(Number);
  }

  if (!day || !month || !year || year < 1000 || year > 9999) return false;

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

// Convertir fecha de dd/mm/yyyy o mm/dd/yyyy a yyyy-mm-dd
const toISODate = dateString => {
  const parts = dateString.split("/");
  let day, month, year;

  if (currentLocale === "us") {
    [month, day, year] = parts.map(Number);
  } else {
    [day, month, year] = parts.map(Number);
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
};

// Formatear la fecha para mostrar
const syncFormatted = () => {
  if (!model.value) {
    formatted.value = "";
    return;
  }

  const date = new Date(model.value);
  if (isNaN(date.getTime())) {
    formatted.value = "";
    return;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  if (currentLocale === "us") {
    formatted.value = `${month}/${day}/${year}`; // mm/dd/yyyy
  } else {
    formatted.value = `${day}/${month}/${year}`; // dd/mm/yyyy
  }
};

// Filtrar entrada del usuario
const filterNumericInput = event => {
  let value = event.target.value.replace(/[^0-9]/g, ""); // Solo números
  let maxLength = 10; // Para dd/mm/yyyy o mm/dd/yyyy

  if (currentLocale === "us") {
    // mm/dd/yyyy
    if (value.length > 2) value = value.slice(0, 2) + "/" + value.slice(2); // Barra tras mes
    if (value.length > 5) value = value.slice(0, 5) + "/" + value.slice(5); // Barra tras día
  } else {
    // dd/mm/yyyy
    if (value.length > 2) value = value.slice(0, 2) + "/" + value.slice(2); // Barra tras día
    if (value.length > 5) value = value.slice(0, 5) + "/" + value.slice(5); // Barra tras mes
  }

  if (value.length > maxLength) value = value.slice(0, maxLength); // Limitar a 10 caracteres

  // Validar si la fecha está completa
  if (value.length === maxLength) {
    if (!isValidDate(value)) {
      event.target.value = formatted.value; // Volver al valor anterior
      return;
    }
    // Convertir a formato yyyy-mm-dd y actualizar model
    const isoDate = toISODate(value);
    model.value = isoDate;
  }

  event.target.value = value;
};

// Observar cambios en model
watch(model, val => {
  if (val) {
    const date = new Date(val);
    const formattedDate =
      currentLocale === "us"
        ? `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
            date.getDate()
          ).padStart(2, "0")}/${date.getFullYear()}`
        : `${String(date.getDate()).padStart(2, "0")}/${String(
            date.getMonth() + 1
          ).padStart(2, "0")}/${date.getFullYear()}`;

    if (isValidDate(formattedDate)) {
      emit("update:value", val);
    } else {
      model.value = ""; // Limpiar si no es válida
      emit("update:value", "");
    }
  } else {
    emit("update:value", "");
  }
});

// Observar cambios en value
watch(
  () => props.value,
  val => {
    model.value = val;
    syncFormatted();
  }
);

// Observar cambios en la ruta
watch(
  () => route.path,
  () => {
    syncFormatted(); // Reformatear si cambia el idioma
  }
);

function openNativePicker(event) {
  if (event && (event.type === "click" || event.type === "keydown")) {
    try {
      real.value?.showPicker?.();
    } catch (error) {
      const clickEvent = new Event("click", { bubbles: true });
      real.value?.dispatchEvent(clickEvent);
    }
  }
}

syncFormatted();
</script>

<style>
.sr-only {
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.input-text {
  @media screen and (max-width: 700px) {
    font-size: 0.8rem;
  }
}
</style>
